import { useTranslation } from "react-i18next";
import { History } from "lucide-react";
import { AUDIT_ACTION_LABEL_KEY } from "../constants/approvalConstants";
import { formatStamp } from "../constants/claimConstants";

/**
 * The approval history on Screen 6 — every action taken on the claim so far,
 * oldest first, with both levels present once each has acted.
 *
 * Reason is shown verbatim where one was recorded: Reject and Send Back always
 * carry one, Approve only if the approver chose to add a comment. An action
 * the label map does not know is rendered as it came back rather than dropped,
 * so a value added on the server side never silently disappears from an audit
 * trail.
 */
export function ClaimAuditTrail({ actions = [], loading = false }) {
  const { t } = useTranslation();

  if (loading) {
    return (
      <p className="px-4 sm:px-6 py-6 text-sm text-[var(--color-text-secondary)]">
        {t("conveyanceApproval.loadingHistory")}
      </p>
    );
  }

  if (actions.length === 0) {
    return (
      <div className="px-4 sm:px-6 py-8 text-center">
        <div className="mx-auto mb-2.5 h-10 w-10 rounded-2xl bg-[var(--color-workspace)]/60 grid place-items-center text-[var(--color-text-secondary)]">
          <History className="h-5 w-5" />
        </div>
        <p className="text-sm text-[var(--color-text-secondary)]">
          {t("conveyanceApproval.noHistory")}
        </p>
      </div>
    );
  }

  return (
    <ol className="divide-y divide-[var(--color-text-border)]/35">
      {actions.map((entry, index) => {
        const labelKey = AUDIT_ACTION_LABEL_KEY[entry.action];
        return (
          <li
            key={entry.actionId ?? `${entry.action}-${index}`}
            className="px-4 sm:px-6 py-3.5 flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-4"
          >
            <div className="sm:w-[64px] shrink-0">
              {entry.approvalLevel != null ? (
                <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-[var(--color-workspace)]/80 text-[11px] font-bold text-[var(--color-primary)]">
                  {t("conveyanceApproval.levelShort", { level: entry.approvalLevel })}
                </span>
              ) : null}
            </div>

            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-foreground">
                {labelKey ? t(labelKey) : entry.action}
                <span className="font-normal text-[var(--color-text-secondary)]">
                  {" — "}
                  {entry.actorName || t("conveyanceApproval.unknownActor")}
                  {entry.actorRoleAtAction ? ` (${entry.actorRoleAtAction})` : ""}
                </span>
              </p>
              {entry.reasonComment ? (
                <p className="mt-1 text-[13px] text-foreground/85 leading-relaxed whitespace-pre-wrap">
                  {entry.reasonComment}
                </p>
              ) : null}
            </div>

            <p className="sm:text-right text-[11px] text-[var(--color-text-secondary)] shrink-0">
              {formatStamp(entry.actionOn, t)}
            </p>
          </li>
        );
      })}
    </ol>
  );
}

export default ClaimAuditTrail;
